import { useState, useEffect, useCallback } from 'react'
import {
  TrendingUp, RefreshCw, Search, ThumbsUp, ThumbsDown, Minus,
} from 'lucide-react'
import { clsx } from 'clsx'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie,
} from 'recharts'
import { api } from '../lib/api'

const BRAND_COLORS: Record<string, string> = {
  'Microsoft': '#0078d4', 'Google': '#4285f4', 'Amazon': '#ff9900', 'Meta': '#0668E1',
  'Anthropic': '#D4A574', 'Apple': '#555555', 'NVIDIA': '#76B900', 'OpenAI': '#10a37f',
  'AMD': '#ED1C24', 'Intel': '#0071C5', 'Tesla': '#CC0000', 'TSMC': '#E31937',
  'CrowdStrike': '#FF0000', 'Cloudflare': '#f48120', 'Salesforce': '#00A1E0',
}

const POSITIVE_WORDS = ['beat', 'surge', 'rally', 'gain', 'rise', 'launch', 'record', 'upgrade', 'boost', 'growth', 'strong', 'breakthrough', 'innovative', 'profit', 'revenue growth', 'partnership', 'expands']
const NEGATIVE_WORDS = ['fall', 'drop', 'crash', 'plunge', 'decline', 'loss', 'miss', 'downgrade', 'weak', 'layoff', 'breach', 'hack', 'vulnerability', 'fine', 'lawsuit', 'antitrust', 'delay', 'cancel']

function analyseSentiment(title: string, summary: string): 'positive' | 'negative' | 'neutral' {
  const text = (title + ' ' + summary).toLowerCase()
  const pos = POSITIVE_WORDS.filter(w => text.includes(w)).length
  const neg = NEGATIVE_WORDS.filter(w => text.includes(w)).length
  if (pos > neg) return 'positive'
  if (neg > pos) return 'negative'
  return 'neutral'
}

const tooltipStyle = { backgroundColor: '#18181c', border: '1px solid #26262c', borderRadius: 8, fontSize: 11 }

export default function SentimentTrackingPage() {
  const [articles, setArticles] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/api/news/feed', { params: { limit: 500 } })
      setArticles(data.articles || [])
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  // Analyse sentiment per entity
  const TRACKED = ['NVIDIA', 'Microsoft', 'Google', 'Amazon', 'Apple', 'Meta', 'Anthropic', 'OpenAI', 'AMD', 'Intel', 'Tesla', 'TSMC', 'CrowdStrike', 'Cloudflare', 'Salesforce']

  const entitySentiment: Record<string, { positive: number; negative: number; neutral: number; total: number }> = {}

  for (const entity of TRACKED) {
    entitySentiment[entity] = { positive: 0, negative: 0, neutral: 0, total: 0 }
  }

  for (const article of articles) {
    const text = ((article.title || '') + ' ' + (article.summary || '')).toLowerCase()
    const sentiment = analyseSentiment(article.title || '', article.summary || '')

    for (const entity of TRACKED) {
      if (text.includes(entity.toLowerCase())) {
        entitySentiment[entity][sentiment]++
        entitySentiment[entity].total++
      }
    }
  }

  const sortedEntities = Object.entries(entitySentiment)
    .filter(([_, s]) => s.total > 0)
    .sort((a, b) => b[1].total - a[1].total)
    .filter(([name]) => !searchTerm || name.toLowerCase().includes(searchTerm.toLowerCase()))

  const chartData = sortedEntities.map(([name, s]) => ({
    name: name.length > 12 ? name.slice(0, 12) + '..' : name,
    fullName: name,
    positive: s.positive,
    negative: s.negative,
    neutral: s.neutral,
  }))

  // Overall sentiment
  const totalPos = sortedEntities.reduce((s, [_, e]) => s + e.positive, 0)
  const totalNeg = sortedEntities.reduce((s, [_, e]) => s + e.negative, 0)
  const totalNeu = sortedEntities.reduce((s, [_, e]) => s + e.neutral, 0)
  const pieData = [
    { name: 'Positive', value: totalPos, fill: '#10b981' },
    { name: 'Negative', value: totalNeg, fill: '#f43f5e' },
    { name: 'Neutral', value: totalNeu, fill: '#64748b' },
  ]

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <TrendingUp size={20} className="text-emerald" /> Sentiment Tracking
          </h1>
          <p className="text-[10px] text-text-secondary">
            Coverage sentiment for {sortedEntities.length} companies from {articles.length} articles
          </p>
        </div>
        <button type="button" onClick={fetchData} className="btn-secondary text-xs">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3">
        <div className="stat-card py-2 px-3 border border-emerald/20">
          <div className="flex items-center gap-2"><ThumbsUp size={14} className="text-emerald" /><span className="stat-label text-[9px]">Positive</span></div>
          <span className="text-lg font-bold text-emerald">{totalPos}</span>
        </div>
        <div className="stat-card py-2 px-3 border border-rose/20">
          <div className="flex items-center gap-2"><ThumbsDown size={14} className="text-rose" /><span className="stat-label text-[9px]">Negative</span></div>
          <span className="text-lg font-bold text-rose">{totalNeg}</span>
        </div>
        <div className="stat-card py-2 px-3">
          <div className="flex items-center gap-2"><Minus size={14} className="text-text-muted" /><span className="stat-label text-[9px]">Neutral</span></div>
          <span className="text-lg font-bold text-text-muted">{totalNeu}</span>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
        <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Filter companies..." className="input pl-7 py-1.5 text-[11px]" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Stacked bar chart */}
        <div className="lg:col-span-2 card p-4">
          <h2 className="text-xs font-semibold text-text-primary mb-3">Sentiment by Company</h2>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 20 }}>
                <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                <YAxis type="category" dataKey="name" width={100} tick={{ fill: '#94a3b8', fontSize: 9 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="positive" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} name="Positive" />
                <Bar dataKey="neutral" stackId="a" fill="#475569" name="Neutral" />
                <Bar dataKey="negative" stackId="a" fill="#f43f5e" radius={[0, 4, 4, 0]} name="Negative" />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-text-muted text-center py-16">Loading...</p>}
        </div>

        {/* Pie + entity list */}
        <div className="space-y-3">
          <div className="card p-4">
            <h2 className="text-xs font-semibold text-text-primary mb-2">Overall Sentiment</h2>
            <ResponsiveContainer width="100%" height={150}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={35} outerRadius={60} dataKey="value" />
                <Tooltip contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="card p-4">
            <h2 className="text-xs font-semibold text-text-primary mb-2">Entity Breakdown</h2>
            <div className="space-y-1.5 max-h-[250px] overflow-y-auto">
              {sortedEntities.map(([name, s]) => {
                const posPct = s.total > 0 ? Math.round(s.positive / s.total * 100) : 0
                const negPct = s.total > 0 ? Math.round(s.negative / s.total * 100) : 0
                return (
                  <div key={name} className="flex items-center gap-2 text-[10px]">
                    <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: BRAND_COLORS[name] || '#64748b' }} />
                    <span className="text-text-primary w-20 truncate">{name}</span>
                    {/* Mini sentiment bar */}
                    <div className="flex-1 flex h-1.5 rounded-full overflow-hidden bg-surface-hover">
                      {s.positive > 0 && <div className="bg-emerald h-full" style={{ width: `${posPct}%` }} />}
                      {s.neutral > 0 && <div className="bg-slate-600 h-full" style={{ width: `${100 - posPct - negPct}%` }} />}
                      {s.negative > 0 && <div className="bg-rose h-full" style={{ width: `${negPct}%` }} />}
                    </div>
                    <span className="text-text-muted font-mono w-6 text-right">{s.total}</span>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
